import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const EMPTY_FORM = {
  legal_name: "",
  dba: "",
  website: "",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  loc_label: "",
  loc_line1: "",
  loc_city: "",
  loc_state: "",
  loc_zip: "",
  is_test_data: false,
};

export default function DealersList() {
  const [dealers, setDealers] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    api
      .get(`/dealers${showArchived ? "?include_archived=true" : ""}`)
      .then((res) => setDealers(res.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(load, [showArchived]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        legal_name: form.legal_name,
        dba: form.dba,
        website: form.website,
        is_test_data: form.is_test_data,
        primary_contact: form.contact_name
          ? { name: form.contact_name, email: form.contact_email || null, phone: form.contact_phone }
          : null,
        // Layer 1: onboarding always creates Location #1 (default showroom + ship-to)
        initial_location: {
          type: "showroom",
          label: form.loc_label,
          timezone: "America/New_York",
          address: {
            line1: form.loc_line1,
            line2: "",
            city: form.loc_city,
            state: form.loc_state,
            postal_code: form.loc_zip,
            country: "US",
          },
        },
      };
      const { data } = await api.post("/dealers", payload);
      toast.success(`Dealer ${data.dealer_code} created${data.primary_user ? ` — invitation queued for ${data.primary_user.email} (delivery status in Users tab)` : ""}`);
      setOpen(false);
      setForm(EMPTY_FORM);
      navigate(`/admin/dealers/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <div data-testid="admin-dealers-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Accounts</p>
          <h1 className="mt-3 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">
            Dealers
          </h1>
        </div>
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-warmgrey">
            <Switch
              checked={showArchived}
              onCheckedChange={setShowArchived}
              data-testid="show-archived-dealers-toggle"
            />
            Show archived
          </label>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="new-dealer-button"
                className="h-11 bg-terracotta text-white transition-all duration-200 hover:-translate-y-[1px] hover:bg-terracotta-hover"
              >
                <Plus className="mr-2 h-4 w-4" /> New Dealer
              </Button>
            </DialogTrigger>
          <DialogContent className="border-border bg-white sm:max-w-lg sm:!max-h-[90dvh] sm:!overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-medium text-charcoal">
                Create dealer account
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="mt-2 space-y-5" data-testid="new-dealer-form">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Legal business name *</Label>
                <Input required data-testid="dealer-legal-name-input" value={form.legal_name} onChange={set("legal_name")} className="h-11 bg-white focus-visible:ring-terracotta" />
              </div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">DBA / Showroom name</Label>
                  <Input data-testid="dealer-dba-input" value={form.dba} onChange={set("dba")} className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Website</Label>
                  <Input data-testid="dealer-website-input" value={form.website} onChange={set("website")} className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
              </div>
              <label className="flex items-center justify-between rounded-md border border-border bg-sand px-4 py-3" data-testid="dealer-test-toggle-row">
                <span>
                  <span className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Test / Demo Dealer</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">Marks this dealer and everything created from it as test data — permanently removable later from Admin → Test Data. Never use for real accounts.</span>
                </span>
                <Switch checked={form.is_test_data} onCheckedChange={(v) => setForm((f) => ({ ...f, is_test_data: v }))} data-testid="dealer-test-toggle" />
              </label>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Primary contact name</Label>
                <Input data-testid="dealer-contact-name-input" value={form.contact_name} onChange={set("contact_name")} className="h-11 bg-white focus-visible:ring-terracotta" />
              </div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Contact email</Label>
                  <Input type="email" data-testid="dealer-contact-email-input" value={form.contact_email} onChange={set("contact_email")} className="h-11 bg-white focus-visible:ring-terracotta" />
                  <p className="text-xs text-warmgrey" data-testid="dealer-invite-note">With a contact email, the primary contact automatically receives an email invitation with portal access and a one-time password.</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Contact phone</Label>
                  <Input data-testid="dealer-contact-phone-input" value={form.contact_phone} onChange={set("contact_phone")} className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
              </div>
              <div className="border-t border-border pt-5">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">
                  Initial showroom location <span className="normal-case text-muted-foreground">(required — becomes Location #1 and the default ship-to)</span>
                </p>
                <div className="mt-5 space-y-5">
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Label *</Label>
                      <Input required data-testid="dealer-loc-label-input" value={form.loc_label} onChange={set("loc_label")} placeholder="e.g. Miami Showroom" className="h-11 bg-white focus-visible:ring-terracotta" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Address *</Label>
                      <Input required data-testid="dealer-loc-line1-input" value={form.loc_line1} onChange={set("loc_line1")} placeholder="Street address" className="h-11 bg-white focus-visible:ring-terracotta" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <div className="col-span-2 space-y-2">
                      <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">City *</Label>
                      <Input required data-testid="dealer-loc-city-input" value={form.loc_city} onChange={set("loc_city")} className="h-11 bg-white focus-visible:ring-terracotta" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">State *</Label>
                      <Input required data-testid="dealer-loc-state-input" value={form.loc_state} onChange={set("loc_state")} className="h-11 bg-white focus-visible:ring-terracotta" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">ZIP *</Label>
                      <Input required data-testid="dealer-loc-zip-input" value={form.loc_zip} onChange={set("loc_zip")} className="h-11 bg-white focus-visible:ring-terracotta" />
                    </div>
                  </div>
                </div>
              </div>
              <Button
                type="submit"
                disabled={saving}
                data-testid="create-dealer-submit"
                className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create dealer"}
              </Button>
            </form>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="mt-12 overflow-x-auto rounded-md border border-border bg-white shadow-[0_2px_12px_rgba(26,26,24,0.04)]">
        <Table data-testid="dealers-table">
          <TableHeader>
            <TableRow className="border-border">
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Code</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Business</TableHead>
              <TableHead className="hidden text-xs uppercase tracking-widest text-muted-foreground sm:table-cell">Primary Contact</TableHead>
              <TableHead className="hidden text-xs uppercase tracking-widest text-muted-foreground sm:table-cell">Locations</TableHead>
              <TableHead className="hidden text-xs uppercase tracking-widest text-muted-foreground sm:table-cell">Users</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center text-sm text-muted-foreground">
                  Loading dealers…
                </TableCell>
              </TableRow>
            ) : dealers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center text-sm text-muted-foreground" data-testid="dealers-empty">
                  No dealers yet. Create the first authorized dealer account.
                </TableCell>
              </TableRow>
            ) : (
              dealers.map((d) => (
                <TableRow
                  key={d.id}
                  data-testid={`dealer-row-${d.dealer_code}`}
                  onClick={() => navigate(`/admin/dealers/${d.id}`)}
                  className="cursor-pointer border-border transition-colors duration-200 hover:bg-sand"
                >
                  <TableCell className="py-4 font-mono text-xs text-warmgrey">{d.dealer_code}</TableCell>
                  <TableCell className="py-4">
                    <p className="font-medium text-charcoal">
                      {d.dba || d.legal_name}
                      {d.is_test_data && <span className="badge-status badge-reserved ml-2" data-testid={`dealer-test-badge-${d.dealer_code}`}>TEST</span>}
                      {d.pending_setup_count > 0 && (
                        <span className="badge-status badge-reserved ml-2" data-testid={`dealer-onboarding-${d.dealer_code}`}>
                          Onboarding pending
                        </span>
                      )}
                    </p>
                    {d.dba && <p className="text-xs text-muted-foreground">{d.legal_name}</p>}
                  </TableCell>
                  <TableCell className="hidden py-4 text-sm text-warmgrey sm:table-cell">
                    {d.primary_contact?.name || "—"}
                    {d.primary_contact?.email && (
                      <p className="text-xs text-muted-foreground">{d.primary_contact.email}</p>
                    )}
                  </TableCell>
                  <TableCell className="num hidden py-4 text-sm text-warmgrey sm:table-cell">{d.locations?.length || 0}</TableCell>
                  <TableCell className="num hidden py-4 text-sm text-warmgrey sm:table-cell" data-testid={`dealer-users-count-${d.dealer_code}`}>
                    {d.archived_users_count ? `${d.users_count} active · ${d.archived_users_count} archived` : d.users_count}
                  </TableCell>
                  <TableCell className="py-4">
                    <span className={`badge-status ${d.archived ? "badge-neutral" : d.status === "active" ? "badge-available" : "badge-neutral"}`}>
                      {d.archived ? "archived" : d.status}
                    </span>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
